import { useEffect, useState, type ReactNode } from 'react';
import { ActionModal, type ActionModalTone } from '../shared/ActionModal';

interface ReasonCaptureModalProps {
    /** Controls modal visibility. */
    open: boolean;
    /** Tone determines accent + button colors. */
    tone: ActionModalTone;
    /** Modal title (e.g. "Request a revision" / "Decline case"). */
    title: string;
    /** Sub-line under the title (e.g. "{name} · Case #2847 · returns to ortho team"). */
    subtitle: string;
    /** Textarea label. */
    noteLabel: string;
    /** Textarea placeholder copy. */
    notePlaceholder: string;
    /** Helper callout shown below the textarea. */
    helperText: string;
    /** Submit-button label. */
    submitLabel: string;
    /** True while the parent's submit handler is in flight. */
    isSubmitting: boolean;
    /** Handler invoked with the trimmed note when the user clicks submit. */
    onSubmit: (note: string) => void;
    onClose: () => void;
    /** Optional slot rendered above the textarea, e.g. the ClarificationHistory. */
    beforeNote?: ReactNode;
}

/** Captures a single reason/note for Decline or Request revision; the V2 endpoints require 5+ characters. */
export function ReasonCaptureModal({
    open,
    tone,
    title,
    subtitle,
    noteLabel,
    notePlaceholder,
    helperText,
    submitLabel,
    isSubmitting,
    onSubmit,
    onClose,
    beforeNote
}: ReasonCaptureModalProps) {
    const [note, setNote] = useState('');

    // Reset the textarea each time the modal re-opens so it doesn't leak the
    // previous attempt's text into a fresh capture.
    useEffect(() => {
        if (open) setNote('');
    }, [open]);

    const trimmed = note.trim();

    return (
        <ActionModal
            open={open}
            tone={tone}
            title={title}
            subtitle={subtitle}
            helperText={helperText}
            submitLabel={submitLabel}
            canSubmit={trimmed.length >= 5 && !isSubmitting}
            isSubmitting={isSubmitting}
            onSubmit={() => onSubmit(trimmed)}
            onClose={onClose}
        >
            {beforeNote}
            <div>
                <label
                    htmlFor="reason-modal-note"
                    className="block text-xs font-semibold text-gray-700 mb-1.5"
                >
                    {noteLabel} <span className="font-normal text-gray-400">(required)</span>
                </label>
                <textarea
                    id="reason-modal-note"
                    rows={4}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={notePlaceholder}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 resize-none disabled:opacity-50"
                    autoFocus
                />
            </div>
        </ActionModal>
    );
}
